import { useTranslation } from 'react-i18next';

import { Avatar, Badge, Circle, HStack, Spacer, Text, VStack } from '@bonusly/design-system';

import type { BizyPeopleBrowserEntry } from '../../../lib/bizy/types';
import { getBizyNavigationRowStyles } from '../lib/bizy-navigation-row-styles';

// Reusable, presentational "Bizy noticed" list (BIZY-636). Renders the condensed
// People Browser rows — avatar + unread blip, name, 2-line headline, and a
// "Fading soon" badge. An unread row pairs the blip with a full-contrast name;
// once seen, the blip goes and the name dims with it. Intentionally headless of
// data/navigation so the same component backs the compact web menu and the
// fullscreen sidebar today, and the desktop sidebar later (BIZY-642).

// The sidebar shows at most 5 recent entries (backend also caps this).
const MAX_ENTRIES = 5;

export interface BizyPeopleListProps {
	entries: BizyPeopleBrowserEntry[];
	/** Invoked when a row is activated (e.g. to open the person / dive in). */
	onSelectPerson?: (entry: BizyPeopleBrowserEntry) => void;
	/**
	 * Observed-moment id to visually highlight. Keyed on the moment (not the
	 * person) so that when a person has multiple entries only the selected one
	 * highlights. Takes precedence over `focusedPersonId`.
	 */
	selectedObservedMomentId?: string | null;
	/**
	 * Person id to highlight when no specific moment is selected — used by the
	 * notification-peek path, which points at a person (not a single moment).
	 */
	focusedPersonId?: string | null;
}

export function BizyPeopleList({ entries, onSelectPerson, selectedObservedMomentId, focusedPersonId }: BizyPeopleListProps) {
	const { t } = useTranslation('admin_bizy');

	if (entries.length === 0) return null;

	const normalizedSelectedMomentId = selectedObservedMomentId || null;
	const hasSelectedMoment = normalizedSelectedMomentId != null;

	// A specific selected moment wins; otherwise fall back to the peek's
	// person-level highlight.
	const isEntryFocused = (entry: BizyPeopleBrowserEntry) =>
		hasSelectedMoment ? normalizedSelectedMomentId === entry.observedMomentId : focusedPersonId != null && focusedPersonId === entry.person.id;

	const visibleEntries = entries.slice(0, MAX_ENTRIES);
	// The peek points at a person, so it highlights every row that person owns —
	// but `aria-current` has to stay singular or the nav set reads as having
	// several current destinations. Only the first highlighted row claims it.
	const currentEntryIndex = visibleEntries.findIndex(isEntryFocused);

	return (
		<VStack data-component="bizy-people-list" align="stretch" spacing={1}>
			{visibleEntries.map((entry, index) => {
				const isFocused = isEntryFocused(entry);
				const interactive = Boolean(onSelectPerson);

				return (
					<HStack
						key={entry.observedMomentId}
						as={interactive ? 'button' : 'div'}
						align="flex-start"
						spacing={2.5}
						w="100%"
						textAlign="left"
						px={2}
						py={2}
						borderRadius="md"
						{...getBizyNavigationRowStyles(isFocused, interactive)}
						cursor={interactive ? 'pointer' : 'default'}
						transition="background-color 150ms ease"
						onClick={interactive ? () => onSelectPerson?.(entry) : undefined}
						data-unread={entry.unread ? 'true' : 'false'}
						// Only when true: Chakra's `_selected` matches on attribute presence,
						// so a "false" value would still style the row as selected.
						data-selected={isFocused ? 'true' : undefined}
						// The chat row hands `current` over to whichever person row is
						// selected, so this has to pick it up — otherwise assistive tech is
						// told nothing in the nav set is current.
						aria-current={index === currentEntryIndex ? 'page' : undefined}
					>
						<Avatar name={entry.person.name ?? undefined} src={entry.person.avatarUrl ?? undefined} boxSize="24px" flexShrink={0} />
						{/* No row gap, and tight leading on both lines: the name and its
						    preview should read as one block, not two stacked paragraphs. */}
						<VStack align="stretch" spacing={0} minW={0} flex={1}>
							{/* Name + unread blip share one line; the preview below spans that width. */}
							<HStack spacing={2} align="center">
								{/* Shrinks (and only then ellipsises) rather than taking a fixed
								    share of the row — the Spacer below absorbs the slack. */}
								<Text size="md" variant="normal" color={entry.unread ? 'secondary.fg' : 'text.softer'} noOfLines={1} minW={0} lineHeight="shorter">
									{entry.person.name}
								</Text>
								{entry.unread && (
									<>
										<Spacer />
										<Circle size="8px" bg="primary.fg" flexShrink={0} aria-label={t('menu.unreadIndicator')} data-testid="bizy-people-unread-blip" />
									</>
								)}
							</HStack>
							<Text fontSize="xs" color="text.softest" noOfLines={2} lineHeight="short">
								{entry.headline}
							</Text>
							{/* Below the headline so it never competes with the name for room. */}
							{entry.fadingSoon && (
								<Badge
									bg="coralAlpha.100"
									color="coral.500"
									// The raw coral palette has no dark variant, and coral.500 on
									// this tint over gray.900 lands at 2.98:1 — under AA for 12px.
									_dark={{ bg: 'coralAlpha.150', color: 'coral.300' }}
									fontSize="xs"
									textTransform="none"
									fontWeight="medium"
									borderRadius="full"
									px={2}
									py="1px"
									alignSelf="flex-start"
									mt={1}
								>
									{t('menu.fadingSoon')}
								</Badge>
							)}
						</VStack>
					</HStack>
				);
			})}
		</VStack>
	);
}
